import { Extension } from '@tiptap/core';
import { EditorState } from '@tiptap/pm/state';

export interface IOnSplitParams {
  updatedContent: any;
  separated: any;
}

/**
 * Точка разреза, безопасная для inline-узлов с содержимым.
 *
 * Разрез ровно по позиции курсора рвёт такой узел надвое: из одной TK-метки
 * получаются две половинки с одинаковым id и битым литералом. Поэтому, если
 * курсор стоит внутри inline-узла, точка съезжает к его началу — узел уходит
 * в отделяемую часть целиком.
 *
 * Проверка намеренно не знает про TK: она про любой inline-узел с содержимым,
 * так что следующий такой узел получит защиту бесплатно.
 */
function safeSplitPosition(state: EditorState, pos: number): number {
  const $pos = state.doc.resolve(pos);

  for (let { depth } = $pos; depth > 0; depth -= 1) {
    if ($pos.node(depth).isInline) {
      return $pos.before(depth);
    }
  }

  return pos;
}

interface SplitOptions {
  onLowerPartSplit: (params: IOnSplitParams) => void;
  onUpperPartSplit: (params: IOnSplitParams) => void;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    splitText: {
      cutOffLowerPart: () => ReturnType;
      cutOffUpperPart: () => ReturnType;
    };
  }
}

export const TextSplitterExtension = Extension.create<SplitOptions>({
  name: 'splitText',

  addOptions() {
    return {
      onLowerPartSplit: () => {},
      onUpperPartSplit: () => {},
    };
  },

  addCommands() {
    return {
      cutOffLowerPart:
        () =>
        ({ tr, state, dispatch }) => {
          if (!dispatch) return true;

          const from = safeSplitPosition(state, state.selection.from);
          const { doc } = state;

          // Получаем контент от начала до курсора
          const beforeContent = doc.cut(0, from).toJSON();
          // Получаем контент от курсора до конца
          const afterContent = doc.cut(from, doc.content.size).toJSON();

          // Оставляем только контент до курсора
          const newTr = tr.replaceWith(0, doc.content.size, doc.slice(0, from).content);
          dispatch(newTr);

          // Возвращаем удалённый контент (после курсора) через коллбэк
          this.options.onLowerPartSplit({ updatedContent: beforeContent, separated: afterContent });

          return true;
        },

      cutOffUpperPart:
        () =>
        ({ tr, state, dispatch }) => {
          if (!dispatch) return true;

          const from = safeSplitPosition(state, state.selection.from);
          const { doc } = state;

          // Получаем контент от начала до курсора
          const beforeContent = doc.cut(0, from).toJSON();
          // Получаем контент от курсора до конца
          const afterContent = doc.cut(from, doc.content.size).toJSON();

          // Оставляем только контент после курсора
          const newTr = tr.replaceWith(0, doc.content.size, doc.slice(from).content);
          dispatch(newTr);

          // Возвращаем удалённый контент (до курсора) через коллбэк
          this.options.onUpperPartSplit({ updatedContent: afterContent, separated: beforeContent });

          return true;
        },
    };
  },
});
